import { Switch } from 'antd';
import { useState } from 'react';

import { useRateSlabs, type RateSlabRow } from '@/api/masters';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { formatAmount } from '@core/format/money';
import type { Session } from '@core/session/types';
import { RateCardPage } from './rateCards/RateCardPage';
import {
  cardProblems,
  groupIntoCards,
  ladder,
  ownerLabels,
  stepLabel,
  type RateCard,
} from './rateCards/cards';

/**
 * What the shop charges, and for how long (spec Section 8 rebuilt, decision 2).
 *
 * <p>
 * <strong>This is the screen without which no booking can be taken at all.</strong> `BookingService`
 * refuses an unpriced garment rather than summing it as zero — an unpriced rate is unfinished setup
 * and a zero is a deliberate freebie, and a shop that cannot tell them apart hands out a sherwani
 * for nothing. Until this existed, a rate card came from the development seeder or from a raw API
 * call, which meant a real tenant could not set its own prices.
 * </p>
 *
 * <p>
 * <strong>The list is of cards, not of bands.</strong> The API is band-level because that is the
 * grain a rate is published at, but a band alone says nothing: "₹9,000 up to 7 days" is only
 * meaningful beside the ₹6,500 three-day band above it. See `rateCards/cards.ts`, which does the
 * grouping and owns every rule this screen renders.
 * </p>
 *
 * <p>
 * Pricing is inherited — a card on a category prices every design beneath it, and the nearest level
 * with a card wins the whole card rather than contributing bands to a merge. So a shop with four
 * cards has priced its whole catalogue, and this list is correctly short.
 * </p>
 */
export function RateCardsPage({ session }: { session: Session }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<
    { ownerKind: RateCard['ownerKind']; ownerId: number; shared: boolean } | 'new' | null
  >(null);

  // The same placeholder the booking screens carry until the tenant's own currency reaches the
  // session. `formatAmount` deliberately prepends nothing — the symbol is the money quad's business.
  const currency = '₹';

  const slabs = useRateSlabs(session.branch?.id, includeInactive);
  const rows: RateSlabRow[] = slabs.data ?? [];
  const cards = groupIntoCards(rows);

  // A full page rather than a drawer, and it replaces the list rather than sitting over it: a card
  // is a ladder of bands with their own controls, an owner and a preview, which a 560px drawer
  // showed a third of at a time. Same move `StockIntakePage` and `NewBookingPage` made.
  if (editing) {
    return (
      <RateCardPage
        session={session}
        // Every visible band, so the page resolves its own card from the live query rather than from
        // a row captured when it was opened — which is a snapshot no refetch reaches.
        rows={rows}
        {...(editing === 'new' ? {} : { initial: editing })}
        currency={currency}
        onClose={() => setEditing(null)}
      />
    );
  }

  const columns: DataViewColumn<RateCard>[] = [
    {
      title: 'Prices',
      key: 'owner',
      primary: true,
      render: (_, card) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{card.ownerName}</span>

          <StatusBadge tone="neutral">{ownerLabels[card.ownerKind]}</StatusBadge>

          {/*
            Only the exception is badged, as on every other master screen. Company-wide is the
            ordinary shape of a rate card and badging it would be noise on every row.
          */}
          {!card.shared && <StatusBadge tone="brand">This branch</StatusBadge>}
        </div>
      ),
    },
    {
      title: 'The ladder',
      key: 'bands',
      render: (_, card) => <Ladder card={card} currency={currency} />,
    },
    {
      title: 'In force',
      key: 'state',
      width: 190,
      render: (_, card) => <Standing card={card} />,
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, card) => (
        <div className="flex justify-end">
          {/*
            One action, and it is not `RateCard:Edit`-gated: the page is where a card is *read* as
            well as changed, and it disables its own controls per permission. Someone who may only
            view still needs to see what the shop charges.
          */}
          <IconAction
            icon="icon icon-pencil-line"
            label={`Open the ${card.ownerName} rate card`}
            onClick={() =>
              setEditing({
                ownerKind: card.ownerKind,
                ownerId: card.ownerId,
                shared: card.shared,
              })
            }
          />
        </div>
      ),
    },
  ];

  if (slabs.isError) {
    return <ApiErrorAlert error={slabs.error} />;
  }

  const unpriceable = cards.filter((card) => !card.shadowed && cardProblems(card).length > 0);

  return (
    <>
      <PageHeading
        title="Rate cards"
        subtitle="What a hire costs, by how long it runs. A booking is priced from these and refused without one."
        actions={
          <>
            <label className="text-default inline-flex cursor-pointer items-center gap-2 text-[13px]">
              <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
              Show retired bands
            </label>

            <WriteAction session={session} required="RateCard:Create">
              {(disabled) => (
                <Button
                  variant="primary"
                  icon="icon icon-plus"
                  shortcut="create"
                  disabled={disabled}
                  onClick={() => setEditing('new')}
                >
                  New rate card
                </Button>
              )}
            </WriteAction>
          </>
        }
      />

      {unpriceable.length > 0 && (
        <div className="mb-3">
          <Notice tone="warning" title="Some cards cannot price every booking">
            {unpriceable.map((card) => (
              <p key={card.key}>
                <b className="text-gray-900">{card.ownerName}</b> — {cardProblems(card)[0]}
              </p>
            ))}
          </Notice>
        </div>
      )}

      <Card flush className="p-4 pb-0 max-sm:pb-1">
        <DataView<RateCard>
          columns={columns}
          dataSource={cards}
          loading={slabs.isLoading}
          rowKey="key"
          empty={
            <EmptyState
              icon="icon icon-receipt-indian-rupee"
              title="Nothing is priced yet"
              hint="No booking can be taken until something is. Start with a card on each top-level category — every design beneath inherits it, and one design can override its own later."
              action={
                <WriteAction session={session} required="RateCard:Create">
                  {(disabled) => (
                    <Button
                      variant="primary"
                      icon="icon icon-plus"
                      disabled={disabled}
                      onClick={() => setEditing('new')}
                    >
                      New rate card
                    </Button>
                  )}
                </WriteAction>
              }
            />
          }
        />
      </Card>
    </>
  );
}

/** The card as a shop reads it — each rung, what it covers, what it costs. */
function Ladder({ card, currency }: { card: RateCard; currency: string }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {ladder(card.bands).map((step) => (
        <span
          key={step.band.id}
          className={`border-border-color inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[11.5px] ${
            step.band.isActive ? 'bg-white' : 'bg-light opacity-60'
          }`}
          title={step.band.isActive ? undefined : 'Retired — resolution skips it'}
        >
          <span className="text-default">{stepLabel(step)}</span>
          <b className="font-mono-nums text-gray-900">
            {currency}
            {formatAmount(step.band.amount)}
          </b>
          {step.band.perExtraDay !== null && (
            <span className="text-default">
              +{currency}
              {formatAmount(step.band.perExtraDay)}/day
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * Whether this card is what a booking would actually be priced from.
 *
 * The one thing a list of cards cannot leave unsaid. Resolution takes a level's branch-local rows
 * if there are any and its shared ones otherwise, so a company card at a branch that has published
 * its own prices nothing here — and there is no sign of that anywhere in its own row.
 */
function Standing({ card }: { card: RateCard }) {
  if (card.shadowed) {
    return (
      <span className="text-warning text-[12px]">
        <i className="icon icon-corner-down-right me-1" />
        Overridden at this branch
      </span>
    );
  }

  const problems = cardProblems(card);

  if (problems.length > 0) {
    return <span className="text-warning text-[12px]">{problems[0]}</span>;
  }

  return (
    <span className="text-default text-[12px]">
      {card.overrides ? 'In force here, over the company card' : 'In force'}
    </span>
  );
}
